import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { WorkshopV2 } from '../../interfaces/workshop.interface';
import { db } from '../../firebase';
import WorkshopCard from './components/WorkshopCard';
import SkeletonWorkshopCard from './components/SkeletonWorkshopCard';
import AddWorkshopPopup from './components/AddWorkshopPopup';

const WorkshopsPage = () => {
  const [workshops, setWorkshops] = useState<WorkshopV2[]>([]);
  const [visiblePeriod, setVisiblePeriod] = useState(28); // en jours
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);

  const fetchWorkshops = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date();
      const limitDate = new Date();
      limitDate.setDate(today.getDate() + visiblePeriod);

      const q = query(
        collection(db, 'workshops_v2'),
        where('date', '>=', Timestamp.fromDate(today)),
        where('date', '<=', Timestamp.fromDate(limitDate))
      );

      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      })) as WorkshopV2[];

      setWorkshops(data);
    } catch (err) {
      console.error('Erreur lors du chargement des ateliers :', err);
    } finally {
      setLoading(false);
    }
  }, [visiblePeriod]);

  useEffect(() => {
    fetchWorkshops();
  }, [fetchWorkshops]);

  const handleLoadMore = () => {
    setVisiblePeriod((prev) => prev + 14); // Ajoute 2 semaines
  };

  const handleLoadLess = () => {
    if (visiblePeriod <= 14) return;
    setVisiblePeriod((prev) => prev - 14); // Retire 2 semaines
  };

  return (
    <div className="h-full flex flex-col p-6 gap-4">
      {/* Zone haute : liste + contrôles */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            Ateliers à venir dans les {visiblePeriod / 7} semaines{' '}
          </h2>
          <div>
            <button
              onClick={handleLoadLess}
              className="text-sm text-[#623413] hover:underline pr-5"
            >
              Charger moins
            </button>
            <button
              onClick={handleLoadMore}
              className="text-sm text-[#623413] hover:underline"
            >
              Charger plus
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col gap-4 pr-2">
          {loading
            ? [...Array(4)].map((_, i) => <SkeletonWorkshopCard key={i} />)
            : workshops.map((workshop) => (
                <WorkshopCard
                  key={workshop.id}
                  workshop={workshop}
                  onUpdated={fetchWorkshops}
                />
              ))}
        </div>
      </div>

      {/* Zone basse fixe : boutons */}
      <div className="h-[30px] flex items-center gap-4 border-t pt-4">
        <button
          onClick={() => setShowPopup(true)}
          className="bg-[#623413] text-white px-4 py-2 rounded hover:bg-[#4e2910]"
        >
          Ajouter un atelier
        </button>

        <button className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300 text-sm">
          Visualiser dans le calendrier
        </button>
      </div>

      {showPopup && (
        <AddWorkshopPopup
          onCreated={fetchWorkshops}
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
};

export default WorkshopsPage;
